import React, { useState, useCallback } from 'react';
import { useApolloClient } from '@apollo/client/react';
import { REFRESH_MFA_BACKUP_TOKENS, RefreshMfaBackupTokensResponse } from '../graphql/mfa';
import {
  Item,
  ItemMedia,
  ItemContent,
  ItemTitle,
  ItemDescription,
  ItemFooter,
} from '@repo/ui/Item';

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type ButtonComponentType = React.ComponentType<any>;
// eslint-disable-next-line @typescript-eslint/no-explicit-any
type DialogComponentType = React.ComponentType<any>;

export interface BackupTokensCardProps {
  hasAnyMfaEnabled: boolean;
  onRefetch: () => void;
  ButtonComponent: ButtonComponentType;
  DialogComponent: DialogComponentType;
}

export function BackupTokensCard({
  hasAnyMfaEnabled,
  onRefetch,
  ButtonComponent,
  DialogComponent,
}: BackupTokensCardProps) {
  const client = useApolloClient();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [tokens, setTokens] = useState<string[] | null>(null);
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [copied, setCopied] = useState(false);

  const handleRegenerate = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const result = (await client.mutate({
        mutation: REFRESH_MFA_BACKUP_TOKENS,
      })) as { data?: RefreshMfaBackupTokensResponse };
      const backupTokens = result.data?.refreshMfaBackupTokensForMe.mfaBackupTokens;
      if (backupTokens) {
        setTokens(backupTokens.map((t) => t.token));
        setShowConfirmDialog(false);
        onRefetch();
      }
    } catch (err) {
      setError('Failed to generate backup tokens. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [client, onRefetch]);

  const handleCopyTokens = useCallback(async () => {
    if (!tokens) return;
    const tokenText = tokens.join('\n');
    try {
      await navigator.clipboard.writeText(tokenText);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      setError('Failed to copy tokens to clipboard.');
    }
  }, [tokens]);

  const handleCloseTokens = useCallback(() => {
    setTokens(null);
    setCopied(false);
  }, []);

  const handleDownloadTokens = useCallback(() => {
    if (!tokens) return;
    const tokenText = tokens.join('\n');
    const blob = new Blob([tokenText], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = 'backup-codes.txt';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  }, [tokens]);

  if (!hasAnyMfaEnabled) {
    return null;
  }

  return (
    <>
      <Item>
        <ItemMedia className="flex justify-center items-center w-10 h-10 bg-blue-100 rounded-lg dark:bg-blue-900">
          <span className="text-blue-600 material-symbols-outlined dark:text-blue-400">key</span>
        </ItemMedia>
        <ItemContent>
          <ItemTitle>Backup Codes</ItemTitle>
          <ItemDescription>
            Use backup codes to sign in if you lose access to your other MFA methods. Each code can
            only be used once.
          </ItemDescription>
        </ItemContent>
        <ItemFooter className="justify-end">
          <ButtonComponent
            type="button"
            variant="secondary"
            size="sm"
            onClick={() => setShowConfirmDialog(true)}
          >
            Generate New Codes
          </ButtonComponent>
        </ItemFooter>
      </Item>

      {showConfirmDialog && (
        <DialogComponent
          isOpen={true}
          setIsOpen={(open: boolean) => !open && setShowConfirmDialog(false)}
          title="Generate New Backup Codes"
          centered
        >
          <div className="flex flex-col gap-6 items-center mx-auto w-full max-w-md">
            <div className="text-center">
              <div className="flex justify-center mb-4">
                <div className="flex items-center justify-center w-16 h-16 rounded-full bg-amber-100 dark:bg-amber-900/30">
                  <span className="text-3xl text-amber-600 material-symbols-outlined dark:text-amber-400">
                    warning
                  </span>
                </div>
              </div>
              <p className="text-grey-600 dark:text-grey-300">
                This will invalidate all existing backup codes and generate new ones. Make sure to
                save the new codes in a secure location.
              </p>
            </div>
            <div className="flex flex-col gap-3 w-full">
              <ButtonComponent
                type="button"
                onClick={handleRegenerate}
                loading={loading}
                disabled={loading}
              >
                Generate New Codes
              </ButtonComponent>
              <ButtonComponent
                type="button"
                variant="tertiary"
                onClick={() => setShowConfirmDialog(false)}
                disabled={loading}
              >
                Cancel
              </ButtonComponent>
            </div>
          </div>
        </DialogComponent>
      )}

      {tokens && (
        <DialogComponent
          isOpen={true}
          setIsOpen={(open: boolean) => !open && handleCloseTokens()}
          title="Your New Backup Codes"
          centered
        >
          <div className="flex flex-col gap-6 items-center mx-auto w-full max-w-md">
            {error && (
              <div className="w-full p-3 text-sm text-red-800 bg-red-100 dark:bg-red-900/30 dark:text-red-300 rounded-lg">
                {error}
              </div>
            )}

            <div className="p-3 w-full text-center rounded-lg sm:p-4 bg-grey-100 dark:bg-grey-800">
              <div className="flex flex-col gap-2 items-center mb-3 sm:flex-row sm:justify-between sm:gap-3">
                <p className="text-sm font-medium text-center text-grey-700 dark:text-grey-300 sm:flex-1 sm:text-left">
                  Save these codes in a secure location
                </p>
                <ButtonComponent
                  type="button"
                  variant="tertiary"
                  size="sm"
                  onClick={handleCopyTokens}
                >
                  <span className="text-base material-symbols-outlined">
                    {copied ? 'check' : 'content_copy'}
                  </span>
                  {copied ? 'Copied!' : 'Copy all'}
                </ButtonComponent>
              </div>
              <div className="w-full mt-4 max-h-[15vh] overflow-y-auto">
                <div className="grid grid-cols-2 gap-1.5 pr-2 sm:gap-2">
                  {tokens.map((token, index) => (
                    <code
                      key={index}
                      className="px-2 py-2 font-mono text-xs bg-white rounded border select-all sm:px-3 sm:text-sm dark:bg-grey-900 border-grey-200 dark:border-grey-700 break-all"
                    >
                      {token}
                    </code>
                  ))}
                </div>
              </div>
            </div>

            <ButtonComponent
              type="button"
              variant="secondary"
              size="sm"
              onClick={handleDownloadTokens}
              className="w-full"
            >
              <span className="text-base material-symbols-outlined">download</span>
              Download
            </ButtonComponent>

            <div className="w-full p-3 bg-amber-50 dark:bg-amber-900/20 rounded-lg border border-amber-200 dark:border-amber-800">
              <div className="flex gap-2 justify-center items-start">
                <span className="material-symbols-outlined text-amber-600 dark:text-amber-400 text-base mt-0.5">
                  warning
                </span>
                <p className="text-sm text-center text-amber-800 dark:text-amber-200">
                  These codes will only be shown once. Make sure to save them before closing this
                  dialog.
                </p>
              </div>
            </div>

            <div className="flex flex-col gap-3 w-full">
              <ButtonComponent type="button" onClick={handleCloseTokens}>
                I&apos;ve Saved My Codes
              </ButtonComponent>
            </div>
          </div>
        </DialogComponent>
      )}
    </>
  );
}
